import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

interface LightboxImage {
  src: string;
  alt?: string;
  label?: string;
}

interface ImageLightboxProps {
  open: boolean;
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export function ImageLightbox({
  open,
  images,
  currentIndex,
  onClose,
  onIndexChange,
}: ImageLightboxProps) {
  const current = images[currentIndex];
  if (!current) return null;

  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < images.length - 1;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      slotProps={{
        paper: {
          sx: {
            bgcolor: 'rgba(0,0,0,0.95)',
            maxWidth: '95vw',
            maxHeight: '95vh',
            m: 1,
            overflow: 'hidden',
          },
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          color: 'white',
          zIndex: 1,
          bgcolor: 'rgba(0,0,0,0.4)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Image */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: 400,
          minHeight: 300,
          p: 2,
          pt: 5,
          pb: 8,
        }}
      >
        <Box
          component="img"
          src={current.src}
          alt={current.alt ?? ''}
          sx={{
            maxWidth: '90vw',
            maxHeight: '80vh',
            objectFit: 'contain',
            borderRadius: 1,
          }}
        />
      </Box>

      {/* Bottom bar: arrows + label */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={2}
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          py: 1.5,
          px: 2,
          bgcolor: 'rgba(0,0,0,0.6)',
        }}
      >
        <IconButton
          onClick={() => onIndexChange(currentIndex - 1)}
          disabled={!hasPrev}
          sx={{
            color: 'white',
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
          }}
        >
          <ArrowBackIosNewIcon />
        </IconButton>

        <Typography
          variant="body2"
          sx={{ color: 'white', minWidth: 60, textAlign: 'center' }}
        >
          {currentIndex + 1} / {images.length}
        </Typography>

        <IconButton
          onClick={() => onIndexChange(currentIndex + 1)}
          disabled={!hasNext}
          sx={{
            color: 'white',
            '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
          }}
        >
          <ArrowForwardIosIcon />
        </IconButton>
      </Stack>

      {/* Label below image counter */}
      {current.label && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 48,
            left: 0,
            right: 0,
            textAlign: 'center',
            color: 'rgba(255,255,255,0.7)',
          }}
        >
          {current.label}
        </Typography>
      )}
    </Dialog>
  );
}
